import {
  MdPerson,
  MdEdit,
  MdDelete,
  MdVisibility,
  MdLoyalty,
  MdDescription,
  MdLocationOn,
  MdPhone,
  MdEmail,
} from 'react-icons/md'
import { Button } from '../ui'

function PassengerCard({ passenger, onView, onEdit, onDelete }) {
  const getStatusColor = (status) => {
    switch (status) {
      case 'active':
        return 'bg-success-100 dark:bg-success-900 text-success-600 dark:text-success-400'
      case 'inactive':
        return 'bg-neutral-100 dark:bg-neutral-800 text-neutral-600 dark:text-neutral-400'
      default:
        return 'bg-neutral-100 dark:bg-neutral-800 text-neutral-600 dark:text-neutral-400'
    }
  }

  const getLoyaltyTierColor = (tier) => {
    switch (tier) {
      case 'platinum':
        return 'bg-purple-100 dark:bg-purple-900 text-purple-600 dark:text-purple-400'
      case 'gold':
        return 'bg-yellow-100 dark:bg-yellow-900 text-yellow-600 dark:text-yellow-400'
      case 'silver':
        return 'bg-gray-100 dark:bg-gray-800 text-gray-600 dark:text-gray-400'
      case 'premium':
        return 'bg-blue-100 dark:bg-blue-900 text-blue-600 dark:text-blue-400'
      default:
        return 'bg-neutral-100 dark:bg-neutral-800 text-neutral-600 dark:text-neutral-400'
    }
  }

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A'
    return new Date(dateString).toLocaleDateString()
  }

  const getDaysUntilExpiry = (dateString) => {
    if (!dateString) return null
    const expiryDate = new Date(dateString)
    const today = new Date()
    const diffTime = expiryDate - today
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24))
    return diffDays
  }

  const getExpiryStatus = (dateString) => {
    const daysUntil = getDaysUntilExpiry(dateString)
    if (daysUntil === null) return 'unknown'
    if (daysUntil < 0) return 'expired'
    if (daysUntil <= 30) return 'warning'
    return 'good'
  }

  const getExpiryColor = (status) => {
    switch (status) {
      case 'expired':
        return 'text-red-600 dark:text-red-400'
      case 'warning':
        return 'text-warning-600 dark:text-warning-400'
      case 'good':
        return 'text-success-600 dark:text-success-400'
      default:
        return 'text-neutral-500 dark:text-neutral-400'
    }
  }

  const passportExpiryStatus = getExpiryStatus(passenger.passport?.expiryDate)
  const hasSpecialRequirements =
    passenger.specialRequirements?.mobilityAssistance?.required ||
    (passenger.specialRequirements?.medicalConditions &&
      passenger.specialRequirements.medicalConditions.length > 0) ||
    (passenger.specialRequirements?.dietaryRestrictions &&
      passenger.specialRequirements.dietaryRestrictions.length > 0)

  return (
    <div className="bg-surface-light dark:bg-surface-dark rounded-xl shadow-md dark:shadow-md p-6 hover:shadow-lg dark:hover:shadow-lg transition-shadow duration-200">
      {/* Header */}
      <div className="flex items-start justify-between mb-4">
        <div className="flex items-center space-x-3">
          <div className="w-12 h-12 bg-primary-100 dark:bg-primary-900 rounded-lg flex items-center justify-center">
            <MdPerson className="text-primary-600 dark:text-primary-400 text-xl" />
          </div>
          <div>
            <h3 className="font-semibold text-neutral-900 dark:text-white">
              {passenger.firstName} {passenger.lastName}
            </h3>
            <p className="text-sm text-neutral-600 dark:text-neutral-400">{passenger.email}</p>
          </div>
        </div>
        <div className="flex items-center space-x-1">
          <Button variant="ghost" size="sm" onClick={onView} className="p-1" title="View Details">
            <MdVisibility className="text-lg" />
          </Button>
          <Button variant="ghost" size="sm" onClick={onEdit} className="p-1" title="Edit Passenger">
            <MdEdit className="text-lg" />
          </Button>
          <Button
            variant="ghost"
            size="sm"
            onClick={onDelete}
            className="p-1 text-red-600 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/20"
            title="Delete Passenger"
          >
            <MdDelete className="text-lg" />
          </Button>
        </div>
      </div>

      {/* Status and Basic Info */}
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center space-x-2">
          <span
            className={`px-2 py-1 text-xs font-medium rounded-full ${getStatusColor(passenger.status)}`}
          >
            {passenger.status?.charAt(0).toUpperCase() + passenger.status?.slice(1)}
          </span>
          {passenger.loyaltyProgram?.tier && (
            <span
              className={`px-2 py-1 text-xs font-medium rounded-full ${getLoyaltyTierColor(passenger.loyaltyProgram.tier)}`}
            >
              {passenger.loyaltyProgram.tier.charAt(0).toUpperCase() +
                passenger.loyaltyProgram.tier.slice(1)}
            </span>
          )}
        </div>
        <p className="text-sm text-neutral-600 dark:text-neutral-400">{passenger.nationality}</p>
      </div>

      {/* Contact Information */}
      <div className="space-y-2 mb-4">
        <div className="flex items-center space-x-2 text-sm">
          <MdPhone className="text-neutral-500 dark:text-neutral-400" />
          <span className="text-neutral-900 dark:text-white">{passenger.phone || 'N/A'}</span>
        </div>
        <div className="flex items-center space-x-2 text-sm">
          <MdEmail className="text-neutral-500 dark:text-neutral-400" />
          <span className="text-neutral-900 dark:text-white">{passenger.email}</span>
        </div>
        <div className="flex items-center space-x-2 text-sm">
          <MdLocationOn className="text-neutral-500 dark:text-neutral-400" />
          <span className="text-neutral-900 dark:text-white">
            {passenger.contactInfo?.address?.city}, {passenger.contactInfo?.address?.country}
          </span>
        </div>
      </div>

      {/* Key Information */}
      <div className="space-y-3 mb-4">
        <div className="flex justify-between text-sm">
          <span className="text-neutral-600 dark:text-neutral-400">Date of Birth:</span>
          <span className="text-neutral-900 dark:text-white font-medium">
            {formatDate(passenger.dateOfBirth)}
          </span>
        </div>

        <div className="flex justify-between text-sm">
          <span className="text-neutral-600 dark:text-neutral-400">Gender:</span>
          <span className="text-neutral-900 dark:text-white font-medium">
            {passenger.gender || 'N/A'}
          </span>
        </div>

        <div className="flex justify-between text-sm">
          <span className="text-neutral-600 dark:text-neutral-400">Loyalty Points:</span>
          <span className="text-neutral-900 dark:text-white font-medium flex items-center space-x-1">
            <MdLoyalty className="text-yellow-500" />
            <span>{passenger.loyaltyProgram?.points?.toLocaleString() || '0'}</span>
          </span>
        </div>
      </div>

      {/* Passport Information */}
      {passenger.passport && (
        <div className="space-y-2 mb-4">
          <div className="flex items-center space-x-2 text-sm">
            <MdDescription className="text-neutral-500 dark:text-neutral-400" />
            <span className="text-neutral-600 dark:text-neutral-400">Passport:</span>
            <span className="text-neutral-900 dark:text-white font-medium">
              {passenger.passport.number}
            </span>
          </div>

          {passenger.passport.expiryDate && (
            <div className="flex justify-between items-center text-xs">
              <span className="text-neutral-600 dark:text-neutral-400">Expires:</span>
              <span className={`font-medium ${getExpiryColor(passportExpiryStatus)}`}>
                {formatDate(passenger.passport.expiryDate)}
                {passportExpiryStatus === 'expired' && ' (Expired)'}
                {passportExpiryStatus === 'warning' &&
                  ` (${getDaysUntilExpiry(passenger.passport.expiryDate)} days)`}
              </span>
            </div>
          )}
        </div>
      )}

      {/* Special Requirements */}
      {hasSpecialRequirements && (
        <div className="mt-4 pt-4 border-t border-neutral-200 dark:border-neutral-700">
          <p className="text-xs text-neutral-600 dark:text-neutral-400 mb-2">
            Special Requirements:
          </p>
          <div className="flex flex-wrap gap-1">
            {passenger.specialRequirements?.mobilityAssistance?.required && (
              <span className="px-2 py-1 text-xs bg-blue-100 dark:bg-blue-900 text-blue-700 dark:text-blue-300 rounded">
                Mobility
              </span>
            )}
            {passenger.specialRequirements?.medicalConditions?.map((condition, index) => (
              <span
                key={index}
                className="px-2 py-1 text-xs bg-red-100 dark:bg-red-900 text-red-700 dark:text-red-300 rounded"
              >
                {condition}
              </span>
            ))}
            {passenger.specialRequirements?.dietaryRestrictions?.map((restriction, index) => (
              <span
                key={index}
                className="px-2 py-1 text-xs bg-green-100 dark:bg-green-900 text-green-700 dark:text-green-300 rounded"
              >
                {restriction}
              </span>
            ))}
          </div>
        </div>
      )}

      {/* Preferences */}
      {passenger.preferences && (
        <div className="mt-4 pt-4 border-t border-neutral-200 dark:border-neutral-700">
          <p className="text-xs text-neutral-600 dark:text-neutral-400 mb-2">Preferences:</p>
          <div className="flex flex-wrap gap-1">
            {passenger.preferences.seatPreference && (
              <span className="px-2 py-1 text-xs bg-neutral-100 dark:bg-neutral-800 text-neutral-700 dark:text-neutral-300 rounded">
                {passenger.preferences.seatPreference}
              </span>
            )}
            {passenger.preferences.mealPreference && (
              <span className="px-2 py-1 text-xs bg-neutral-100 dark:bg-neutral-800 text-neutral-700 dark:text-neutral-300 rounded">
                {passenger.preferences.mealPreference}
              </span>
            )}
            {passenger.preferences.preferredLanguage && (
              <span className="px-2 py-1 text-xs bg-neutral-100 dark:bg-neutral-800 text-neutral-700 dark:text-neutral-300 rounded">
                {passenger.preferences.preferredLanguage}
              </span>
            )}
          </div>
        </div>
      )}
    </div>
  )
}

export default PassengerCard
